import type { HotEditSelection } from '../../stores/hotEdit';
import { elementLabel, humanizeComponentName } from './inspectorModel';

/**
 * What the editor panel shows for the selected element, and what it says about elements the
 * change also touches (Prototype Editor UX plan).
 *
 * The panel has no tabs and no modes: it lists only the sections this element actually offers,
 * and it mentions other elements only when the element really is one of several rows drawn from
 * one template, with the real count. Pure, so the rules are tested without a browser.
 */
export type PanelSection =
  | 'text'
  | 'fields'
  | 'typeStyle'
  | 'background'
  | 'spacing'
  | 'corners'
  | 'image'
  | 'icon'
  | 'visible'
  | 'records';

export type PanelPlan = {
  /** The element in product words, for the panel header. */
  title: string;
  sections: PanelSection[];
  /** One line under the title when the element is a row of a repeated list. */
  repeatNote: string | null;
  /** Shown on style sections when the change lands on the shared template. */
  repeatChip: string | null;
  /** Why nothing can be edited, in the founder's words. */
  blockedReason: string | null;
  /** The record the data sheet opens on, when there is one. */
  recordId: string | null;
  /** For a container: what it holds, so the founder goes one level in instead of editing the box. */
  holdsNote: string | null;
};

const STYLE_SECTIONS: PanelSection[] = ['typeStyle', 'background', 'spacing', 'corners', 'icon', 'image', 'visible'];

export function isStyleSection(section: PanelSection): boolean {
  return STYLE_SECTIONS.includes(section);
}

/** Plain-language reason the element cannot be edited here, or null. */
function blockedReason(sel: HotEditSelection): string | null {
  // Chef marks only what the founder may change, so a marked element needs no further test.
  if (sel.editMark) return null;
  if (sel.isShell) {
    return 'This is the page frame - the sidebar, top bar and layout every screen shares. Pick something inside it, or ask Ideoz to change the frame.';
  }
  if (sel.elementKind === 'unsupported') return 'Nothing to edit here. Pick the box around it.';
  if (sel.editTargetKind === 'designSystemOnly') {
    return 'This is part of the shared design, so changing it here would change it everywhere. Pick the box around it, or ask Ideoz.';
  }
  if (sel.editTargetKind === 'missing' || sel.canPersistClasses === false) {
    return "The editor can't tell where this comes from in the code. Ask Ideoz to change it.";
  }
  return null;
}

export function planPanel(sel: HotEditSelection): PanelPlan {
  const title = elementLabel(sel);
  const blocked = blockedReason(sel);
  const row = sel.listRow ?? null;
  const container = sel.listContainer ?? null;
  const has = (s: string) => (sel.editableSections ?? []).includes(s as never);
  const tag = sel.tagName.toLowerCase();

  const sections: PanelSection[] = blocked ? [] : sectionsFor(sel, { row, container, tag, has });
  // Whatever the founder hid, they can always bring back: a hidden heading offers no "visible"
  // section of its own, and without one there was no way to unhide it (UI pass, issue 7).
  if (sel.isHidden && !blocked && !sections.includes('visible')) sections.push('visible');

  const holds =
    !blocked && !container && sections.length > 0 && !sections.includes('text') && (sel.childCount ?? 0) > 0
      ? `Holds ${sel.childCount} ${sel.childCount === 1 ? 'element' : 'elements'} - click one to change its text.`
      : null;

  return {
    title,
    sections,
    holdsNote: holds,
    repeatNote: row
      ? `One of ${row.count} ${rowNoun(sel)} - text and records change only this one.`
      : null,
    repeatChip: row ? `Changes all ${row.count}` : null,
    blockedReason: blocked,
    recordId: container?.firstKey ?? row?.key ?? null,
  };
}

/**
 * The sections an element offers. When Chef marked the element while writing the prototype
 * (`data-edit`), the mark decides: it says what the element is for, which no rule read off tag
 * names and React fibers ever did. Prototypes written before the marks keep those rules.
 */
function sectionsFor(
  sel: HotEditSelection,
  ctx: {
    row: HotEditSelection['listRow'];
    container: HotEditSelection['listContainer'];
    tag: string;
    has: (s: string) => boolean;
  },
): PanelSection[] {
  const { row, container, tag, has } = ctx;
  const sections: PanelSection[] = [];

  switch (sel.editMark) {
    case 'text':
      if (!sel.hasChildElements) sections.push('text');
      sections.push('typeStyle');
      if (row?.key) sections.push('records');
      return sections;
    case 'image':
      return ['image'];
    case 'list':
      return ['records', 'spacing'];
    case 'surface':
    case 'record': {
      const out: PanelSection[] = container ? ['records'] : [];
      out.push('background', 'spacing', 'corners', 'visible');
      if (sel.editMark === 'record' && row?.key && !container) out.push('records');
      return out;
    }
    default:
      break;
  }

  if (container) sections.push('records');
  if (has('content') && !sel.hasChildElements) sections.push('text');
  if (has('form')) sections.push('fields');
  if (tag === 'img') sections.push('image');
  if (tag === 'svg') sections.push('icon');
  if (has('typography')) sections.push('typeStyle');
  if (has('box') && tag !== 'svg') sections.push('background');
  if (has('spacing') || has('layout')) sections.push('spacing');
  if (has('radius') || has('border')) sections.push('corners');
  if (has('layout') || has('box')) sections.push('visible');
  if (row?.key && !container) sections.push('records');
  return sections;
}

/**
 * A component named after a whole screen is not what one row of a list is called. Taken as one,
 * four handover notes were described to the founder as "4 shift handover board pages".
 */
const NAMES_A_SCREEN = /\b(app|board|dashboard|layout|page|screen|view)s?$/;

/**
 * What the repeated thing is called, in the plural: the row's own component ("Alert card" ->
 * "alert cards"), not the element inside it that happens to be selected.
 */
export function rowNoun(sel: HotEditSelection): string {
  const crumb = sel.breadcrumb?.[sel.breadcrumb.length - 1]?.label;
  const noun = crumb ? humanizeComponentName(crumb).toLowerCase() : 'items';
  if (NAMES_A_SCREEN.test(noun)) return 'items';
  return noun.endsWith('s') ? noun : `${noun}s`;
}

/** The tooltip on the "Changes all N" chip. */
export function repeatChipHint(count: number): string {
  return `All ${count} share one design, so this change applies to every one of them. To make one different, ask Ideoz.`;
}
